/**
 * Recommends products based on the user's browsing history.
 * Supports related or complementary product recommendations.
 */
if (!customElements.get("recommendation-by-history")) {
  customElements.define(
    "recommendation-by-history",
    class RecommendationByHistory extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
      }

      disconnectedCallback() {
        this.abortFetch();
      }

      /**
       * Show recommended products if a recently viewed product exists, otherwise hide.
       */
      showContent() {
        this.newestProductId = this.getNewestProductId();
        if (this.newestProductId == null) return this.toggleShow(false);

        if (this.newestProductId === this.preProductId) return;

        this.preProductId = this.newestProductId;
        this.url = this.constructUrl();

        this.toggleShow();

        if (this.url) {
          this.fetchContent(this.url);
        } else {
          this.toggleShow(false);
        }
      }

      /**
       * Get the most recently viewed product ID from browsing history.
       * @returns {string|null}
       */
      getNewestProductId() {
        const recentlyViewedProductIds =
          webvista.retrieveData(RECENTLY_VIEWED_KEY) || [];
        if (recentlyViewedProductIds.length === 0) return null;

        return recentlyViewedProductIds.pop();
      }

      /**
       * Construct the recommendation API URL with the product ID.
       * @returns {string|null}
       */
      constructUrl() {
        let url = this.dataset.url;
        if (!url) return null;

        return `${url}&product_id=${this.newestProductId}`;
      }

      /**
       * Fetch recommended products from the API and render them.
       */
      fetchContent(url) {
        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();
        const signal = this.fetchController.signal;

        webvista
          .fetchHtml(url, signal)
          .then((html) => {
            const sourceRecommendationMain = html.querySelector(
              `#${this.id} .recommendation-main`,
            );

            if (sourceRecommendationMain) {
              this.innerHTML = sourceRecommendationMain.outerHTML;

              webvista.initLazyImages();
              webvista.initTooltips();
            } else {
              this.toggleShow(false);
            }
          })
          .catch((error) => {
            this.toggleShow(false);
          });
      }

      /**
       * Toggle visibility — shows a loading placeholder when visible.
       * @param {boolean} show
       */
      toggleShow(show = true) {
        if (show) {
          // Show loading placeholder while fetching
          this.placeholderTemplate =
            this.placeholderTemplate ||
            this.querySelector(`#Placeholder-Template-${this.section}`);

          this.replaceChildren(
            this.placeholderTemplate.content.cloneNode(true),
          );
        }

        this.setAttribute("aria-hidden", !show);
        this.classList.toggle("hide", !show);
      }

      /**
       * Abort any in-flight fetch request.
       */
      abortFetch() {
        if (this.fetchController) this.fetchController.abort();
      }
    },
  );
}
